<template>
  <div :data-testid="dataTestid" class="h-52 w-full">
    <VChart
      :data-testid="`${dataTestid}-chart`"
      :option="option"
      :theme="OMR_ECHARTS_THEME"
      autoresize
      class="h-full w-full"
      @update-axis-pointer="onAxisPointer"
      @globalout="emit('hover', null)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { OMR_ECHARTS_THEME } from '@/composables/useOmrEchartsTheme'
import { VChart, type OmrEChartsOption } from '@/config/echarts'
import { makeFixed, makeNumber } from '@/helpers/chartFormat'

const props = withDefaults(
  defineProps<{
    categories: string[]
    values: number[]
    counts: number[]
    color: string
    countColor: string
    valueLabel: string
    countLabel: string
    locale: string
    dataTestid?: string
  }>(),
  { dataTestid: 'insights-rating-line-chart' },
)

const emit = defineEmits<{ hover: [index: number | null] }>()

const MIN_RATING = 1
const MAX_RATING = 5
const MIN_SPAN = 1
const HALF_STAR = 0.5

const ratingRange = (values: number[]) => {
  if (!values.length) return { min: MIN_RATING, max: MAX_RATING }
  const low = Math.floor(Math.min(...values) / HALF_STAR) * HALF_STAR
  const high = Math.ceil(Math.max(...values) / HALF_STAR) * HALF_STAR
  const max = Math.min(MAX_RATING, Math.max(high, low + MIN_SPAN))
  const min = Math.max(MIN_RATING, Math.min(low, max - MIN_SPAN))
  return { min, max }
}

interface AxisPointerInfo {
  axisDim: string
  value: number
}

const onAxisPointer = (event: { [key: string]: unknown }) => {
  const axesInfo = (event.axesInfo ?? []) as AxisPointerInfo[]
  const xAxis = axesInfo.find((info) => info.axisDim === 'x')
  emit('hover', xAxis ? xAxis.value : null)
}

const option = computed<OmrEChartsOption>(() => {
  const formatRating = makeFixed(props.locale, 1)
  const formatCount = makeNumber(props.locale)
  const lastIndex = props.values.length - 1
  const lastOnly = (value: number, index: number) => ({ value, symbol: index === lastIndex ? 'circle' : 'none' })
  const range = ratingRange(props.values)

  return {
    tooltip: { trigger: 'axis', showContent: false },
    legend: { bottom: 0, icon: 'circle', itemWidth: 8, itemHeight: 8 },
    grid: { top: 24, left: 8, right: 8, bottom: 28, containLabel: true },
    xAxis: {
      type: 'category',
      data: props.categories,
      boundaryGap: false,
    },
    yAxis: [
      {
        type: 'value',
        min: range.min,
        max: range.max,
        minInterval: HALF_STAR,
        splitLine: { show: false },
        axisLabel: { formatter: (value: number | string) => formatRating(Number(value)) },
      },
      {
        type: 'value',
        scale: true,
        minInterval: 1,
        splitNumber: 3,
        splitLine: { show: false },
        axisLabel: { color: props.countColor, formatter: (value: number | string) => formatCount(Number(value)) },
      },
    ],
    series: [
      {
        type: 'line',
        name: props.valueLabel,
        yAxisIndex: 0,
        color: props.color,
        symbolSize: 7,
        areaStyle: { opacity: 0.1 },
        label: { show: false },
        data: props.values.map(lastOnly),
      },
      {
        type: 'line',
        name: props.countLabel,
        yAxisIndex: 1,
        color: props.countColor,
        symbolSize: 5,
        lineStyle: { type: 'dashed', width: 1.5 },
        label: { show: false },
        data: props.counts.map(lastOnly),
      },
    ],
  }
})
</script>
